import React from 'react';
import { Card, CardBody, CardHeader } from '../../../../components/Atoms/Card';
import Button from '../../../../components/Atoms/Button';
import IconButton from '../../../../components/Atoms/IconButton';
import VideoJobStatusBadge from './VideoJobStatusBadge';
import { useI18n } from '../../../../i18n';
import { isJobActive } from '../hooks/useVideoToImagesHook';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

/**
 * Running + past jobs, read from the persisted job records — so the status is
 * accurate on return even if the user left mid-conversion.
 *
 * Row layout note: every cell is single-line (the target name ellipsizes) and
 * the secondary actions are icon-only. Text buttons here made the action cell
 * wrap its labels mid-word and blew the row height up, especially in Korean.
 */
const VideoJobsList = ({ jobs, jobsError, handlers }) => {
  const { t } = useI18n();

  return (
    <Card className="v2i-card">
      <CardHeader
        title={t('videoToImages.jobs.heading')}
        actions={
          <IconButton icon="fa-sync" title={t('common.refresh')} onClick={handlers.handleGetJobs} />
        }
      />
      <CardBody>
        {jobsError && (
          <div className="v2i-alert v2i-alert--danger" role="alert">
            <i className="fa fa-exclamation-circle mr-1" aria-hidden="true"></i>{jobsError}
          </div>
        )}

        {!jobs.length ? (
          <div className="v2i-empty">
            <i className="fa fa-film v2i-empty__icon" aria-hidden="true"></i>
            <p className="v2i-empty__title mb-1">{t('videoToImages.jobs.empty')}</p>
            <p className="v2i-hint mb-0">{t('videoToImages.jobs.emptyHint')}</p>
          </div>
        ) : (
          <div className="v2i-table-wrap">
            <table className="v2i-table">
              <thead>
                <tr>
                  <th className="v2i-table__job-col">{t('videoToImages.jobs.columnTarget')}</th>
                  <th>{t('videoToImages.jobs.columnStatus')}</th>
                  <th className="v2i-table__progress-col">{t('videoToImages.jobs.columnProgress')}</th>
                  <th>{t('videoToImages.jobs.columnImages')}</th>
                  <th>{t('videoToImages.jobs.columnCreated')}</th>
                  <th className="v2i-table__actions-col">{t('videoToImages.jobs.columnActions')}</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const totals = job.totals || {};
                  const extracted = Number(totals.framesExtracted || 0);
                  const estimated = Number(totals.framesEstimated || 0);
                  const active = isJobActive(job);
                  const hasWarnings = Boolean((job.warnings || []).length);
                  const isDataset = job.targetKind === 'dataset';
                  const percent = job.progressPercent || 0;

                  const targetName = isDataset ? job.datasetName : job.modelName;
                  const targetDetail = isDataset
                    ? t('videoToImages.jobs.datasetTarget')
                    : t('videoToImages.jobs.version', { version: job.version });

                  return (
                    <tr key={job._id}>
                      <td className="v2i-table__job-col">
                        <div className="v2i-table__primary" title={targetName || '-'}>
                          <i
                            className={`fa ${isDataset ? 'fa-database' : 'fa-cube'} v2i-table__icon`}
                            aria-hidden="true"
                          ></i>
                          {targetName || '-'}
                        </div>
                        <div className="v2i-table__secondary">
                          {targetDetail}
                          {' · '}
                          {t('videoToImages.jobs.sources', {
                            done: totals.sourcesDone || 0,
                            total: totals.sources || 0,
                          })}
                        </div>
                      </td>
                      <td>
                        <VideoJobStatusBadge status={job.status} hasWarnings={hasWarnings} />
                      </td>
                      <td className="v2i-table__progress-col">
                        <div className="v2i-progress-cell">
                          <div className="v2i-progress">
                            <div
                              className={`v2i-progress__fill ${active ? 'v2i-progress__fill--active' : ''}`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <span className="v2i-progress-cell__value">{percent}%</span>
                        </div>
                      </td>
                      <td>
                        <span className="v2i-table__primary">
                          {active && estimated
                            ? t('videoToImages.jobs.framesOf', { count: extracted, total: estimated })
                            : t('videoToImages.jobs.frames', { count: extracted })}
                        </span>
                      </td>
                      <td className="v2i-table__secondary">{formatDateTime(job.createdAt)}</td>
                      <td className="v2i-table__actions-col">
                        <div className="v2i-table__actions">
                          <IconButton
                            icon="fa-list-ul"
                            title={t('videoToImages.jobs.viewDetailsTooltip')}
                            onClick={() => handlers.handleOpenDetails(job._id)}
                          />
                          {handlers.canOpenTarget(job) && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              tooltipText={isDataset
                                ? t('videoToImages.jobs.openDatasetTooltip')
                                : t('videoToImages.jobs.openInLabelingTooltip')}
                              onClick={() => handlers.handleOpenTarget(job)}
                            >
                              <i className={`fa ${isDataset ? 'fa-folder-open' : 'fa-tags'} mr-1`} aria-hidden="true"></i>
                              {isDataset
                                ? t('videoToImages.jobs.openDataset')
                                : t('videoToImages.jobs.openInLabeling')}
                            </Button>
                          )}
                          {/* Frames that went straight into a model can still be
                              labeled by the AI, in place. Without this the
                              model path has no AI option at all. */}
                          {!active && !isDataset && job.modelDataInfoId
                            && Number(totals.framesExtracted || 0) > 0 && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              tooltipText={t('videoToImages.jobs.aiLabelTooltip')}
                              onClick={() => handlers.handleOpenVersionBuild(job)}
                            >
                              <i className="fa fa-magic mr-1" aria-hidden="true"></i>
                              {t('videoToImages.jobs.aiLabel')}
                            </Button>
                          )}
                          {active && (
                            <IconButton
                              icon="fa-ban"
                              className="v2i-icon-btn--danger"
                              title={t('videoToImages.jobs.cancelJobTooltip')}
                              onClick={() => handlers.handleCancelJob(job._id)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default VideoJobsList;
